import { alpha, type Components, type Theme as MuiTheme } from '@mui/material';
import { designTokens } from '../themes/design-tokens';

const opacity = designTokens.opacity;

export const chipTheme: Components<MuiTheme> = {
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderWidth: '1px',
        borderStyle: 'solid',
        width: 'max-content',
        borderColor: theme.palette.text.primary,
        variants: [
          {
            props: (props) => props.variant !== 'tag',
            style: {
              borderColor: theme.palette.text.second,
              backgroundColor: theme.palette.background.default,
              '&:hover': {
                opacity: 0.7,
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  opacity.secondHover,
                ),
              },
              '&:active': {
                color: theme.palette.text.primary,
                borderColor: 'theme.palette.text.primary',
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  opacity.secondHover,
                ),
                '& .MuiChip-label': {
                  color: theme.palette.text.primary,
                },
              },
            },
          },
          {
            props: { variant: 'discount' },
            style: {
              // Layout and sizing
              width: '100%',
              justifyContent: 'space-between',
              height: '40px',
              borderRadius: '6px',
              paddingLeft: '8px',
              paddingRight: '8px',
              borderWidth: '2px',
              borderStyle: 'dashed',
              borderColor: theme.palette.secondaryInvert.main,
              backgroundColor: theme.palette.background.blue,
              '&:hover': {
                backgroundColor: theme.palette.background.blue,
                opacity: 1,
              },
            },
          },
          {
            props: (props) => props.variant === 'tag',
            style: {
              '& .MuiChip-deleteIcon': {
                margin: 0,
              },
            },
          },
        ],
      }),
      label: ({ theme }) => ({
        fontFamily: theme.typography.fontFamily,
        variants: [
          {
            props: (props) => props.variant !== 'tag',
            style: {
              gap: theme.spacing(1),
              padding: '0 8px',
              color: theme.palette.text.second,
              '&:disabled': {
                color: theme.palette.text.disabled,
              },
            },
          },
          {
            props: { variant: 'tag' },
            style: {
              gap: theme.spacing(1),
              fontWeight: theme.typography.button.fontWeight,
              color: theme.palette.text.primary,
            },
          },
        ],
      }),
    },
    variants: [
      {
        props: { disabled: true },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.text.disabled, opacity.bg),
          borderColor: theme.palette.text.disabled,
          color: theme.palette.text.disabled,
        }),
      },
      {
        props: { size: 'large' },
        style: {
          height: '36px',
        },
      },
      {
        props: { variant: 'tag', size: 'small' },
        style: ({ theme }) => ({
          height: '24px',
          borderRadius: '6px',
          borderWidth: '1px ',
          padding: theme.spacing(0.5, 2),
          gap: theme.spacing(1),
          fontSize: '14px',
        }),
      },
      {
        props: { variant: 'tag', size: 'medium' },
        style: ({ theme }) => ({
          height: '36px',
          borderRadius: '8px',
          borderWidth: '1px',
          padding: theme.spacing(1.5, 2),
          gap: theme.spacing(1),
          fontSize: '16px',
        }),
      },
      {
        props: { variant: 'tag', color: 'blue' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.blue.main, 0.1),
          borderColor: theme.palette.blue.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'purple' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.purple.main, 0.1),
          borderColor: theme.palette.purple.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'success' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.success.accent, 0.1),
          borderColor: theme.palette.success.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'yellow' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.yellow.main, 0.1),
          borderColor: theme.palette.yellow.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'orange' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.orange.main, 0.1),
          borderColor: theme.palette.orange.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'error' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          borderColor: theme.palette.error.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'textThird' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.text.third, 0.1),
          borderColor: theme.palette.text.third,
          fontWeight: 400,
        }),
      },
      {
        props: { variant: 'tag', color: 'warning' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.warning.main, 0.2),
          borderColor: theme.palette.warning.main,
        }),
      },
      {
        props: { variant: 'tag', color: 'gray' },
        style: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.grey[500], 0.2),
          borderColor: theme.palette.grey[500],
        }),
      },
    ],
  },
};
